import type { MapLayoutFinishSet } from './mapEditorUnreal';

type SendMessageToUnreal = (message: unknown) => void;

// One finish and where it lands.
//
// `roomKey` absent means every standing room, which is the shape the common case collapses to: an
// author finishing a surface is deciding it for the whole floor plan, so "concrete on every wall" is
// one target rather than one per room. A key that is not standing is ignored by the authority
// silently, which is what makes it safe to describe a draft that has moved on.
export interface FinishPreviewTarget {
  slot: string;
  roomKey?: string;
  finish?: string;
  variant: string;
  textureSet: string;
  scalars: Record<string, number>;
  vectors: Record<string, number[]>;
}

// The complete finish state of the draft, applied in this author's client alone.
//
// Declarative rather than incremental, the same way SPAWN_LAYOUT is: `targets` is everything that
// should be finished right now, and a slot the author has just cleared is expressed by its absence.
// The alternative — a "cleared" list alongside the applied one — makes the message a diff, and a
// diff has to be replayed in order to mean anything. This one does not: it is a snapshot, so a
// message lost to a closed socket costs nothing and the next one puts the world right.
export interface SetFinishPreviewPayload {
  action: 'SET_FINISH_PREVIEW';
  version: number;
  // Always true, and sent rather than implied, for the same reason SPAWN_LAYOUT sends it: it is
  // what makes the message a description of the whole state instead of an addition to it.
  clearExisting: true;
  targets: FinishPreviewTarget[];
}

export const SET_FINISH_PREVIEW_VERSION = 1;

/**
 * Builds the preview message for a layout's finishes.
 *
 * The layout-wide entries become targets naming no room, and each override becomes a target naming
 * its own. That is the whole size story for the common case: an author who set one wall finish for a
 * thirty-room chain sends one target of a few hundred bytes rather than thirty, and only the rooms
 * they deliberately made different cost anything.
 *
 * @param finishes - The layout's defaults and its per-placement overrides.
 * @returns The message, ordered layout-wide first and then by room, for a stable wire and tests.
 */
export const buildFinishPreviewPayload = (
  finishes: MapLayoutFinishSet,
): SetFinishPreviewPayload => {
  const layoutWide: FinishPreviewTarget[] = [...finishes.defaults]
    .sort((a, b) => a.slot.localeCompare(b.slot))
    .map((finish) => ({ ...finish }));

  const perRoom = Object.keys(finishes.rooms)
    .sort()
    .flatMap((roomKey) =>
      [...finishes.rooms[roomKey]]
        .sort((a, b) => a.slot.localeCompare(b.slot))
        .map((finish) => ({ ...finish, roomKey })),
    );

  return {
    action: 'SET_FINISH_PREVIEW',
    version: SET_FINISH_PREVIEW_VERSION,
    clearExisting: true,
    targets: [...layoutWide, ...perRoom],
  };
};

/**
 * Applies the draft's finishes in this author's client.
 *
 * @param sendMessageToUnreal - The hub's one path to the instance.
 * @param finishes - The layout's defaults and its per-placement overrides.
 */
export const previewFinishesInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  finishes: MapLayoutFinishSet,
) => {
  sendMessageToUnreal(buildFinishPreviewPayload(finishes));
};
